import React, { Fragment, PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { JsonLd } from 'react-schemaorg';
import get from 'lodash/get';
import map from 'lodash/map';
import each from 'lodash/each';
import findIndex from 'lodash/findIndex';
import { UserProp, perms } from 'auth';
import DataProvider from 'api/data-provider';
import Bem from 'bemHelper';
import absoluteUrl from 'helpers/absoluteUrl';
import { daysDifAndTime, difDays } from 'helpers/formatDate';
import { I18nHoc } from 'helpers/i18n';
import { FavoritesButton, FavoriteWrapper } from 'subsys/favorites';
import Image from 'components/image';
// import AdSense from 'components/adsense';
import Row, { Col } from 'components/row';
import Accordion from 'components/accordion';
import Icon, { iconTypes } from 'components/icon';
import sensor, { SensorProvider } from 'components/sensor';
import Slick from 'components/slick-slider';

import {
  renderCurrentRubrics, renderDevelopers, renderLC, renderOpinion
} from './render-common';

import translates from './article-i18n';

import 'styles/domik/section-article/_article.scss';

const bemClasses = new Bem('article');

@I18nHoc(translates())
export default class ContentRenderer extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    auth: UserProp,
    checkPermission: PropTypes.func,
    publicationId: PropTypes.number,
    publicationUrl: PropTypes.string,
    dataProvider: PropTypes.string,
    dataProviderParams: PropTypes.shape({}),
    dataProviderQuery: PropTypes.shape({}),
    preloaded: PropTypes.shape({}),
    // Advanced elements in header
    headerElements: PropTypes.arrayOf(PropTypes.element),
    // Block with share buttons
    shareElement: PropTypes.element,
    // Block with rating component
    ratingElement: PropTypes.element,
    needSeo: PropTypes.bool
  }

  static defaultProps = {
    needSeo: true
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.getInteractiveComponents = this.getInteractiveComponents.bind(this);

    this.state = {
      accordionContentOpened: true
    };
  }

  componentDidMount() {
    this.mountInteractiveElements();
  }

  componentDidUpdate() {
    this.mountInteractiveElements();
  }

  mountInteractiveElements() {
    const el = this.contentElement;
    if (!el) return;

    each(el.getElementsByClassName('slider-box-content'), (sliderEl, sliderKey) => {
      const imgAttr = sliderEl.getAttribute('data-slider');

      if (!imgAttr) return;

      const imgs = JSON.parse(imgAttr);

      ReactDOM.render((
        <SensorProvider>
          <Slick {...bemClasses('slider', 'article')}>
            {map(imgs, (img, imgKey) => (
              <div {...bemClasses('imgContainer')} key={imgKey}>
                <div {...bemClasses('img')}>
                  <Image
                    {...bemClasses('imgPic')}
                    sensor={this.sensor}
                    image={{ id: img }}
                    group={`sliderBox${sliderKey}`}
                  />
                </div>
              </div>
            ))}
          </Slick>
        </SensorProvider>
      ), sliderEl);
    });
  }

  toggleAccordionContent = () => {
    const { accordionContentOpened } = this.state;
    this.setState({ accordionContentOpened: !accordionContentOpened });
  }

  renderToc(toc) {
    const { i18n } = this;
    const { accordionContentOpened } = this.state;

    if (toc && toc.length > 2) {
      return (
        <div {...bemClasses('text', 'main content')}>
          <h2 children={i18n('contentH2')} />
          <Accordion
            {...bemClasses('accordion', 'content')}
            onPanelClick={this.toggleAccordionContent}
            panelTopic={<span />}
            panelTopicRight={accordionContentOpened ? i18n('hide') : i18n('show')}
            firstlyOpened
          >
            <div>
              <div {...bemClasses('accordionContent')}>
                <ul {...bemClasses('ul')}>
                  {map(toc, (item, key) => (
                    <li {...bemClasses('li')} key={key}>
                      <a {...bemClasses('link')} href={`#${item.href}`} title={item.name} children={item.name} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Accordion>
        </div>
      );
    }

    return null;
  }

  getInteractiveComponents(favData) {
    const { publicationId } = this.props;
    const index = findIndex(favData, { entity_id: publicationId });

    return (
      <FavoritesButton
        {...bemClasses('favorite')}
        project="domik"
        entity="publication"
        entityId={publicationId}
        subgroup="article"
        inFavorites={index > -1}
        hideLabel
      />
    );
  }

  renderPubl = ({ data }) => {
    const {
      className, headerElements, headerOverride,
      shareElement, ratingElement, publicationUrl, needSeo, auth, checkPermission
    } = this.props;
    const publication = data[0] || {};
    const dataLC = get(publication, 'lc_data');
    const {
      image: img_main, name, text, short_text: annotation,
      users_data: developers, time_to_publish, date_ed, id, rubrics, toc, news_views
    } = publication;
    const header = headerOverride ? headerOverride : name;
    const image = typeof img_main === 'string' ? { id: img_main } : img_main;
    const imageArticle = { ...image, renderer: 'orig', alt: header, title: header };

    return (
      <article {...bemClasses({ extra: className })}>
        <div {...bemClasses('top')}>
          <Row {...bemClasses('row', 'articleInfo')}>
            <Col {...bemClasses('col', 'rubric')} allAuto allFlex allColumn allPr={0}>
              {renderCurrentRubrics.call(this, bemClasses, rubrics)}
            </Col>
            <Col {...bemClasses('col', 'time')} allAuto allFlex allColumn allPr={0}>
              <time
                {...bemClasses('time')}
                dateTime={time_to_publish}
                children={daysDifAndTime.call(this, time_to_publish)}
              />
            </Col>
            {headerElements}
          </Row>
          <header {...bemClasses('title')}>
            <div {...bemClasses('text')}><h1 children={header} /></div>
          </header>
        </div>
        <Row {...bemClasses('row', 'main')}>
          <Col {...bemClasses('col', 'article')} allFlex allColumn allPr={0}>
            <Row {...bemClasses('row', 'top')}>
              <Col {...bemClasses('col', 'developers')} allFlex allPr={0}>
                {renderDevelopers.call(this, bemClasses, developers)}
                {renderLC.call(this, bemClasses, dataLC)}
                {renderOpinion.call(this, bemClasses, get(data, '[0]'))}
              </Col>
              {/*
              <Col {...bemClasses('col', 'share')} allAuto allFlex allPr={0}>
                {shareElement}
              </Col>
              */}
            </Row>
            <div {...bemClasses('main')}>
              <Row {...bemClasses('row', 'img')}>
                <Col {...bemClasses('col', 'img')} allPr={0}>
                  <div {...bemClasses('imgContainer')}>
                    <div {...bemClasses('img')}>
                      <Image
                        {...bemClasses('imgPic')}
                        image={imageArticle}
                        fullScreenSwitch="none"
                      />
                    </div>
                  </div>
                  {checkPermission && checkPermission(perms.DOMIK_VIEW_STAT_PUBLICATIONS) &&
                    <div {...bemClasses('onPhoto', 'views')}>
                      <Icon {...bemClasses('icon', 'visibility')} type={iconTypes.visibility} />
                      <span {...bemClasses('text', 'visibility')} children={news_views || 0} />
                    </div>
                  }
                  <FavoriteWrapper project="domik" entity="publication">
                    {({ data }) => this.getInteractiveComponents(data)}
                  </FavoriteWrapper>
                </Col>
              </Row>
              <div {...bemClasses('text', 'main annotation')} children={annotation} />
              {this.renderToc(toc)}
              {/*
              needSeo && <AdSense slot="9002951611" />
              */}
              <div
                {...bemClasses('text', 'main')} dangerouslySetInnerHTML={{ __html: text }}
                ref={el => this.contentElement = el}
              />
            </div>
            <footer {...bemClasses('footer')}>
              <Row {...bemClasses('row', 'footerInfo')}>
                <Col {...bemClasses('col', 'rating')} allAuto allPr={0}>
                  {ratingElement}
                </Col>
                <Col {...bemClasses('col', 'share')} allFlex allPr={0}>
                  {shareElement}
                </Col>
              </Row>
            </footer>
          </Col>
        </Row>
        {needSeo &&
          <JsonLd
            item={{
              '@context': 'https://schema.org',
              '@type': 'Article',
              mainEntityOfPage: absoluteUrl(publicationUrl),
              image: get(image, 'id') ? absoluteUrl(`/images/thumb/full/${image.id}`) : null,
              headline: header || null,
              name: header || null,
              description: annotation || null,
              // author: null,
              datePublished: time_to_publish || null,
              dateModified: date_ed || null,
              // publisher: {
              //   logo: null,
              //   url: null,
              //   image: null,
              //   telephone: null,
              //   address: null,
              // },
              articleBody: text || null,
            }}
          />
        }
      </article>
    );
  }

  render() {
    const {
      preloaded, publicationId, dataProvider, dataProviderParams, dataProviderQuery
    } = this.props;

    return (
      <DataProvider
        url={dataProvider}
        preloaded={preloaded}
        query={{ id: publicationId, ...dataProviderQuery }}
        renderer={this.renderPubl}
        onLoad={() => this.mountInteractiveElements()}
        {...dataProviderParams}
      />
    );
  }
}
